import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState } from '../store';
import { env } from '../../utils/env';
import { setCredentials, logout } from '../slices';
import { refreshSession } from '../../services/cognito';

/**
 * Raw fetchBaseQuery configured with auth headers.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.API_ENDPOINT,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (env.API_KEY) {
      headers.set('x-api-key', env.API_KEY);
    }
    return headers;
  },
});

/**
 * Wrapper that intercepts 401 responses, attempts a Cognito token refresh,
 * and retries the original request with the new token. If the refresh fails,
 * the user is logged out.
 */
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Attempt token refresh
    const refreshResult = await refreshSession();

    if (refreshResult) {
      // Store new tokens
      api.dispatch(
        setCredentials({
          user: {
            id: refreshResult.user.userId,
            email: refreshResult.user.email,
            name: refreshResult.user.name,
            organizationId: refreshResult.user.organizationId,
          },
          token: refreshResult.tokens.idToken,
          accessToken: refreshResult.tokens.accessToken,
          refreshToken: refreshResult.tokens.refreshToken,
        })
      );

      // Retry the original request
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      // Refresh failed — sign out
      api.dispatch(logout());
    }
  }

  return result;
};

/**
 * Base API configuration for RTK Query.
 * All API slices inject endpoints into this base API.
 */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Agent', 'Session', 'Analytics', 'Organization', 'Integration'],
  endpoints: () => ({}),
});
